import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { projectsData } from "./Data";
import "./projectDetails.css";
import OverviewTab from "./tabs/OverviewTab1";
import ArchitectureTab from "./tabs/ArchitectureTab";
import GalleryTab from "./tabs/GalleryTab";
import NotesTab from "./tabs/NotesTab";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "architecture", label: "Architecture" },
  { key: "notes", label: "Notes" },
];

const Icon = ({ name }) => {
  // Inline icons so you do not need an external icon library
  switch (name) {
    case "arrowLeft":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M15 18l-6-6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "external":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M14 3h7v7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 14L21 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M21 14v7H3V3h7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "repo":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M6 3h12v18H6z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M9 7h6M9 11h6M9 15h6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case "demo":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4 6h16v12H4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M10 9l5 3-5 3z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "pipeline":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M6 4h12v6H6zM6 14h12v6H6z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M12 10v4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case "runbook":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M6 2h11l3 3v17H6z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M17 2v4h4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M9 10h8M9 14h8M9 18h8"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
};

const ProjectDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const project = useMemo(() => projectsData.find((p) => p.slug === slug), [slug]);

  useEffect(() => {
    setActiveTab("overview");
  }, [slug]);

  const goBackToProjects = () => navigate({ pathname: "/", hash: "#projects" });

  if (!project) {
    return (
      <section className="project section">
        <div className="container">
          <h2 className="section__title">Project not found</h2>
          <button type="button" className="project__backBtn" onClick={goBackToProjects}>
            <Icon name="arrowLeft" />
            Back to projects
          </button>
        </div>
      </section>
    );
  }

  const links = project.links || {};
  const actions = [
    links.repo ? { key: "repo", label: "Repo", href: links.repo, icon: "repo" } : null,
    links.demo ? { key: "demo", label: "Demo", href: links.demo, icon: "demo" } : null,
    links.pipeline ? { key: "pipeline", label: "Pipeline", href: links.pipeline, icon: "pipeline" } : null,
    links.runbook ? { key: "runbook", label: "Runbook", href: links.runbook, icon: "runbook" } : null,
  ].filter(Boolean);

  const badges = project.badges || project.tags || [];

  return (
    <section className="project section">
      <div className="container">
        <button type="button" className="project__backBtn" onClick={goBackToProjects}>
          <Icon name="arrowLeft" />
          Back
        </button>

        <header className="project__header">
          <div className="project__headerTop">
            <div className="project__heading">
              <h1 className="project__title">{project.title}</h1>
              <p className="project__subtitle">{project.summary}</p>

              {badges && badges.length ? (
                <div className="project__badges" aria-label="Project tags">
                  {badges.map((b) => (
                    <span className="project__badge" key={b}>
                      {b}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            {actions.length ? (
              <div className="project__actions" aria-label="Project links">
                {actions.map((a) => (
                  <a
                    key={a.key}
                    className="project__actionBtn"
                    href={a.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="project__actionIcon" aria-hidden="true">
                      <Icon name={a.icon} />
                    </span>
                    <span>{a.label}</span>
                    <span className="project__actionExt" aria-hidden="true">
                      <Icon name="external" />
                    </span>
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          <div className="project__tabs" role="tablist" aria-label="Project sections">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={activeTab === t.key ? "project__tab active" : "project__tab"}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </header>

        {activeTab === "overview" ? <OverviewTab project={project} /> : null}
        {activeTab === "architecture" ? <ArchitectureTab project={project} /> : null}
        {activeTab === "notes" ? <NotesTab project={project} /> : null}
      </div>
    </section>
  );
};

export default ProjectDetails;
